import { createContext } from '@direct-frontend/modules-context';
import { $internals, FormInstance } from './types';

// eslint-disable-next-line @typescript-eslint/no-explicit-any --- универсальный тип инстанса для всех форм
type AnyFormInstance = FormInstance<any, any, any, any, any, any, any, any, any>;

/**
 * Минимальная структурная форма инстанса, нужная getOrCreateFrozenInstance.
 * Используется как constraint вместо полного FormInstance: полный constraint заставлял TS
 * расширять T до AnyFormInstance из-за контравариантных параметров reinitialize.
 */
type FreezableInstance = { [$internals]: { freezeInitializationId: (initializationId: string) => void } };

/**
 * Обьект formInitializationId - formInstance, хранит ссылку на обьект, на котором висят подписки
 */
export const frozenInstancesContext = createContext<Record<string, AnyFormInstance>>({
    name: `formsFrozenInstances`,
    getInitialValue() {
        return {};
    },
});

/**
 * Создает новый инстанс формы с замороженным ID и сохраняет его в frozenInstances.
 * Generic параметр T позволяет сохранить конкретный тип инстанса.
 * Constraint снят с T намеренно — TS расширяет T extends FormInstance<any,...> до AnyFormInstance
 * при проверке контравариантных параметров reinitialize для конкретных FormInstance<...>.
 */

export const getOrCreateFrozenInstance = <T extends FreezableInstance>(
    initializationId: string,
    formInstance: T
): T => {
    const frozenInstances = frozenInstancesContext.getContext();

    const alreadySavedInstance = frozenInstances[initializationId];

    if (alreadySavedInstance) {
        // eslint-disable-next-line @typescript-eslint/consistent-type-assertions -- гетерогенное хранилище: сохранялось как T
        return alreadySavedInstance as unknown as T;
    }

    const instance = formInstance;
    instance[$internals].freezeInitializationId(initializationId);
    // eslint-disable-next-line @typescript-eslint/consistent-type-assertions -- гетерогенное хранилище инстансов разных форм
    frozenInstances[initializationId] = instance as unknown as AnyFormInstance;
    return instance;
};
